// Element landing pages (/timber, /carpet, /tile, /stone).
// Each entry drives one high-intent local-SEO page: hero, editorial intro,
// specification notes, FAQs (FAQPage schema) and the products of that element.

export const ELEMENTS = [
  {
    slug: "timber",
    name: "Timber",
    discipline: "Timber",
    offerName: "Timber Flooring",
    caption: "Engineered oak · wide-plank · parquet",
    hero: "/timber_interior.jpg",
    heroAlt: "Wide-plank oak timber flooring in a luxury Sydney living room",
    h1: "Timber flooring, Sydney",
    lede:
      "Engineered oak, wide-plank and bespoke parquet — selected in one sitting at our Drummoyne studio, alongside the carpet, tile and stone that will sit beside it.",
    body: [
      "Timber is the element most people picture first and specify last, because the decision is rarely about the board — it is about the board in context. Tone against joinery. Sheen against north light. Board width against the length of the room. We keep a tight edit of engineered oak, wire-brushed and matte-oiled finishes, and bespoke geometries such as herringbone, chevron and Versailles panel, so the choice is between genuinely good options rather than a warehouse of near-identical samples.",
      "Almost every Sydney home we specify for is a slab or a suspended floor with services in it, which is why our range is engineered rather than solid: a real oak wear layer over a dimensionally stable core, so the floor holds its geometry through humid summers and dry winters, and can run over hydronic or electric underfloor heating when the build-up is right.",
    ],
    highlights: [
      {
        title: "Engineered for Australian conditions",
        text: "Multi-ply and hardwood-core constructions that stay flat through Sydney's humidity swing, with wear layers thick enough to be re-sanded at least once.",
      },
      {
        title: "Wide-plank and bespoke geometry",
        text: "Boards from 180mm to 300mm, plus herringbone, chevron and custom Versailles panels cut to your drawing.",
      },
      {
        title: "Finish, tone and sheen matched",
        text: "We view timber against your stone, tile and carpet selections in the same light, so undertones agree instead of fighting.",
      },
    ],
    specNotes: [
      { label: "Typical formats", value: "180–300mm wide planks · herringbone · chevron · Versailles panel" },
      { label: "Construction", value: "Engineered — oak wear layer over stable ply or hardwood core" },
      { label: "Finishes", value: "Wire-brushed, brushed & matte oiled, low-sheen UV lacquer" },
      { label: "Installation", value: "Direct-stick to slab, or float over acoustic underlay per strata requirements" },
      { label: "Underfloor heating", value: "Suitable with correct build-up, surface temperature under 27°C" },
      { label: "Best used in", value: "Living, dining, bedrooms, hallways, studies" },
    ],
    faqs: [
      {
        q: "How much does timber flooring cost in Sydney?",
        a: "Supply for quality engineered oak generally sits in the mid to upper band of the market, with bespoke parquet higher again because of the cutting and setting-out work. Installation, subfloor preparation, levelling and skirting all move the number, so we quote per project rather than per square metre in isolation. Bring your plans to the Drummoyne studio and we will give you a realistic supplied-and-installed range on the day.",
      },
      {
        q: "Engineered or solid timber for a Sydney home?",
        a: "For almost every Sydney project we specify engineered. It carries a genuine oak wear layer but the layered core resists the seasonal movement, cupping and gapping that solid boards show over a concrete slab or in an apartment. Solid still has a place in period restorations where an existing floor is being matched or extended.",
      },
      {
        q: "Can timber flooring go over underfloor heating?",
        a: "Yes, when the floor is engineered and the system is commissioned properly. Surface temperature must stay below 27°C, the heating should be brought up and down gradually before and after installation, and the adhesive must be rated for heated substrates. We confirm the full build-up for your slab before anything is ordered.",
      },
      {
        q: "Do you supply timber flooring for apartments and strata buildings?",
        a: "Yes. Most Sydney strata by-laws require a rated acoustic underlay and a minimum impact-noise result, and many buildings ask for documentation before work starts. We specify a compliant system and provide the acoustic test data you need for your building manager.",
      },
      {
        q: "Which timber suits a dark or a very bright room?",
        a: "In darker rooms we lean to blonde and natural tones with a low sheen so the floor lifts available light without glare. In rooms with strong western or northern glass, mid to deeper tones with a matte finish hold their colour better and hide dust and traffic. We look at both against your actual joinery samples in the studio.",
      },
      {
        q: "Where can I see timber flooring samples near me?",
        a: "At My Floor Studio, 184 Victoria Road, Drummoyne NSW 2047 — by appointment, Monday to Saturday. We serve the Inner West, Lower North Shore, Eastern Suburbs and projects across greater Sydney and regional New South Wales, and we can send curated samples to your project address after a short consultation.",
      },
    ],
    seo: {
      title: "Timber Flooring Sydney · Engineered Oak & Parquet — My Floor Studio",
      description:
        "Engineered oak, wide-plank and bespoke parquet timber flooring in Sydney. Selected by appointment at our Drummoyne studio alongside carpet, tile and stone. Serving the Inner West and all of NSW.",
      keywords:
        "timber flooring sydney, engineered timber flooring sydney, oak flooring sydney, wide plank timber flooring, herringbone parquet sydney, chevron timber flooring, timber flooring drummoyne, timber flooring inner west, engineered oak nsw",
    },
  },
  {
    slug: "carpet",
    name: "Carpet",
    discipline: "Carpet",
    offerName: "Luxury Carpet",
    caption: "Wool · wool-rich · hybrid",
    hero: "https://images.unsplash.com/photo-1724582586495-d050726cf354?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxwbHVzaCUyMHdvdmVuJTIwY2FycGV0JTIwbHV4dXJ5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODU5MjY0MTN8MA&ixlib=rb-4.1.0&q=85",
    heroAlt: "Plush wool carpet in a luxury Sydney bedroom",
    h1: "Luxury carpet, Sydney",
    lede:
      "Wool, wool-rich and high-performance hybrids — loop, cut pile and felted textures chosen for how a room should sound and feel, not just how it photographs.",
    body: [
      "Carpet is the quietest decision in a house and the one people notice every day. It changes the acoustics of a room, the temperature underfoot and how a bedroom reads at seven in the morning. We keep a considered range of New Zealand and Australian wool, wool-rich blends and solution-dyed hybrids, in textures from fine level loop through to deep felted pebble piles.",
      "The specification questions that matter are unglamorous: pile weight, twist, backing, underlay and how the traffic actually moves through the plan. Get those right and a carpet stays handsome for fifteen years. Get them wrong and it flattens in the hallway within two. We work the whole selection back from where each carpet actually sits.",
    ],
    highlights: [
      {
        title: "Wool first, performance where it counts",
        text: "Natural wool for bedrooms and formal rooms; solution-dyed hybrids where kids, pets and sunlight demand more.",
      },
      {
        title: "Texture as a design decision",
        text: "Level loop, chunky felted pebble, cut pile and patterned weaves — each reads differently underfoot and under light.",
      },
      {
        title: "Underlay and acoustics specified",
        text: "The right underlay adds years of life and meaningfully lifts impact-noise performance in apartments.",
      },
    ],
    specNotes: [
      { label: "Fibres", value: "New Zealand & Australian wool · wool-rich blends · solution-dyed nylon hybrids" },
      { label: "Constructions", value: "Level loop, felted pebble loop, cut pile, patterned weave" },
      { label: "Widths", value: "Broadloom 3.66m · custom rugs and stair runners made to size" },
      { label: "Installation", value: "Dual-bond or stretch-in over premium foam or rubber underlay" },
      { label: "Stairs", value: "Suitable constructions specified separately — stairs wear differently to floors" },
      { label: "Best used in", value: "Bedrooms, media rooms, stairs, upper-level living, formal lounges" },
    ],
    faqs: [
      {
        q: "Is wool carpet worth it compared with synthetic?",
        a: "For bedrooms and formal rooms, generally yes. Wool recovers from compression better, holds colour without the plastic sheen synthetics develop, regulates humidity and is naturally flame resistant. Where a room takes heavy family traffic, direct sun or pets, a solution-dyed hybrid is often the smarter specification — and we will say so.",
      },
      {
        q: "Which carpet is best for stairs?",
        a: "Stairs need a tight, dense construction: a firm level loop or a short, high-twist cut pile. Deep plush and long-pile carpets crush and grin at the nose of each tread. Fitting matters as much as fibre, so we specify stair constructions separately from the adjoining floor.",
      },
      {
        q: "How long does good carpet last?",
        a: "A quality wool or wool-rich carpet with the correct underlay, installed properly and vacuumed regularly, should look right for twelve to fifteen years in bedrooms and eight to ten in high-traffic areas. Underlay is the single most under-specified factor in carpet life.",
      },
      {
        q: "Do you supply carpet for apartments with acoustic requirements?",
        a: "Yes. Carpet is usually the easiest way to meet strata impact-noise requirements, and with a rated underlay it comfortably exceeds most by-laws. We can provide the acoustic documentation your building manager or owners corporation asks for.",
      },
      {
        q: "Can carpet be installed over underfloor heating?",
        a: "Usually yes, provided the total thermal resistance of carpet plus underlay stays within the heating system's limit — commonly around 2.5 tog combined. We check the tog values of the exact combination before you order rather than after.",
      },
      {
        q: "Where can I see carpet samples in Sydney?",
        a: "At the My Floor Studio showroom, 184 Victoria Road, Drummoyne NSW 2047, by appointment Monday to Saturday. We work with clients across the Inner West, Lower North Shore, Eastern Suburbs, greater Sydney and regional NSW, and can send samples to your project address.",
      },
    ],
    seo: {
      title: "Luxury Carpet Sydney · Wool & Hybrid Carpet — My Floor Studio",
      description:
        "Wool, wool-rich and high-performance hybrid carpet in Sydney. Loop, cut pile and felted textures selected by appointment at our Drummoyne studio. Serving the Inner West and all of NSW.",
      keywords:
        "carpet sydney, wool carpet sydney, luxury carpet sydney, carpet drummoyne, carpet inner west sydney, stair carpet sydney, broadloom carpet nsw, solution dyed nylon carpet, best carpet for bedrooms",
    },
  },
  {
    slug: "tile",
    name: "Tile",
    discipline: "Tile",
    offerName: "Porcelain & Ceramic Tile",
    caption: "Porcelain · terrazzo · handmade",
    hero: "https://images.unsplash.com/photo-1744025098626-66c0b9cb1ba8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBwb3JjZWxhaW4lMjB0aWxlJTIwYmF0aHJvb20lMjBzcGF8ZW58MHx8fHwxNzg1OTI2NDE0fDA&ixlib=rb-4.1.0&q=85",
    heroAlt: "Large-format porcelain tile in a luxury Sydney bathroom",
    h1: "Porcelain & ceramic tile, Sydney",
    lede:
      "Large-format porcelain, terrazzo, handmade zellige and stone-look bodies — specified with the slip ratings, grout joints and setting-out that make a wet area look effortless.",
    body: [
      "Tile is the most technical element in the house and the one where a beautiful selection can still fail. Slip rating, format, rectification, substrate movement, waterproofing and grout colour all decide whether a bathroom reads as calm or busy. We keep a curated range of large-format porcelain, honed stone-look bodies, terrazzo and handmade glazes, and we specify them against Australian standards rather than a mood board alone.",
      "Large formats have changed bathrooms: fewer joints, fewer shadow lines, a quieter room. They also demand flatter substrates and better setting-out. We work through the tile layout, the fall to waste and the junctions before anything is ordered, because that is where tiled rooms are won.",
    ],
    highlights: [
      {
        title: "Slip ratings specified to AS 4586",
        text: "P-ratings matched to the room — internal floors, wet bathroom floors, external and pool surrounds each need a different result.",
      },
      {
        title: "Large-format done properly",
        text: "Rectified bodies up to 1200x2800mm with minimal joints, plus the substrate and setting-out advice they require.",
      },
      {
        title: "Character glazes and terrazzo",
        text: "Handmade zellige, glazed brick and terrazzo bodies for rooms that want texture instead of a flat field.",
      },
    ],
    specNotes: [
      { label: "Bodies", value: "Rectified porcelain · terrazzo · handmade glazed ceramic" },
      { label: "Formats", value: "300x600 to 1200x2800mm slabs · brick and zellige formats · mosaics" },
      { label: "Finishes", value: "Matte, honed, structured/anti-slip, polished" },
      { label: "Slip rating", value: "Specified to AS 4586 P-ratings per application" },
      { label: "Waterproofing", value: "Wet areas designed to AS 3740 with compatible membrane and adhesive" },
      { label: "Best used in", value: "Bathrooms, laundries, kitchens, entries, alfresco, pool surrounds" },
    ],
    faqs: [
      {
        q: "What slip rating do I need for a bathroom floor in Australia?",
        a: "Internal wet-area floors such as bathrooms and shower bases are typically specified at P3 as a minimum, with P4 or P5 for ramps, external areas and pool surrounds. Internal dry floors are commonly P2. We supply the tested AS 4586 rating for the exact finish you select so it can be documented in your specification.",
      },
      {
        q: "Are large-format tiles a good idea in a small bathroom?",
        a: "Usually yes. Fewer grout joints make a small room read larger and calmer, and a matching wall and floor body carries the eye. The trade-off is substrate flatness and setting-out: large formats are unforgiving on an uneven floor, so allow for levelling in the budget.",
      },
      {
        q: "Porcelain or natural stone for a wet area?",
        a: "Porcelain is dense, non-porous, needs no sealing and can convincingly reproduce marble or limestone. Natural stone gives depth and movement no print can match, but wants sealing and a gentler cleaning regime. In heavily used family bathrooms we usually specify porcelain and save the stone for a feature or a lower-traffic ensuite.",
      },
      {
        q: "Can tile be laid over an existing tiled floor?",
        a: "Sometimes, if the existing floor is sound, well bonded and the extra height works with doors, thresholds and falls to waste. In wet areas the waterproofing usually needs to be reinstated, which means removal. We assess it on site before committing either way.",
      },
      {
        q: "How wide should the grout joint be?",
        a: "Rectified large-format tiles are commonly set with a 2 to 3mm joint, pressed and handmade tiles need more to absorb their size variation, and external areas need wider joints to allow movement. Grout colour matters as much as width — matching it to the tile quietens a room, contrasting it draws the grid.",
      },
      {
        q: "Where can I see tile samples in Sydney?",
        a: "At My Floor Studio, 184 Victoria Road, Drummoyne NSW 2047, by appointment Monday to Saturday. We work across the Inner West, Lower North Shore, Eastern Suburbs, greater Sydney and regional New South Wales, and can send samples to your project address.",
      },
    ],
    seo: {
      title: "Tiles Sydney · Large-Format Porcelain & Terrazzo — My Floor Studio",
      description:
        "Large-format porcelain, terrazzo and handmade tile in Sydney, specified with AS 4586 slip ratings. By appointment at our Drummoyne studio. Serving the Inner West and all of NSW.",
      keywords:
        "tiles sydney, porcelain tiles sydney, large format tiles sydney, bathroom tiles sydney, terrazzo tiles sydney, tile showroom drummoyne, tiles inner west sydney, slip rating tiles as 4586, zellige tiles sydney",
    },
  },
  {
    slug: "stone",
    name: "Stone",
    discipline: "Stone",
    offerName: "Natural Stone Flooring",
    caption: "Limestone · marble · bluestone",
    hero: "/stone_interior.jpg",
    heroAlt: "Honed limestone stone flooring in a luxury Sydney interior",
    h1: "Natural stone flooring, Sydney",
    lede:
      "Limestone, marble, travertine, quartzite and bluestone — chosen slab by slab for the rooms that deserve a material with geology behind it.",
    body: [
      "Stone is the only element in a house that is genuinely old. It carries movement, fossil, vein and tone that no printed surface reproduces, and in the right room it does more work than any other finish. We keep a tight selection of honed limestone, bookmatched marble, brushed travertine, quartzite and Australian and Belgian bluestone, in formats from tile through to large-format slab.",
      "Specifying stone well means being honest about it. It is porous, it patinas, and it needs the right sealer and a sensible cleaning routine. Where a client wants the look with none of the maintenance we will say so and put a porcelain body beside it — but where stone is the right answer, nothing else comes close.",
    ],
    highlights: [
      {
        title: "Selected by slab and batch",
        text: "Vein direction, bookmatching and batch consistency reviewed before anything is committed to a room.",
      },
      {
        title: "Finish drives performance",
        text: "Honed, brushed, sandblasted and tumbled finishes each change slip resistance, patina and how the stone ages.",
      },
      {
        title: "Sealing and care specified",
        text: "Impregnating sealer, pH-neutral cleaning and a realistic maintenance schedule provided with every selection.",
      },
    ],
    specNotes: [
      { label: "Materials", value: "Limestone · marble · travertine · quartzite · basalt & bluestone" },
      { label: "Finishes", value: "Honed, brushed, sandblasted, tumbled, split-face" },
      { label: "Formats", value: "Tile, large-format panel, slab, pavers and pool coping" },
      { label: "Sealing", value: "Impregnating sealer on install, re-applied per traffic and exposure" },
      { label: "Care", value: "pH-neutral cleaners only — acids and generic supermarket products etch stone" },
      { label: "Best used in", value: "Entries, kitchens, living areas, bathrooms, alfresco and pool surrounds" },
    ],
    faqs: [
      {
        q: "Is natural stone flooring practical for a family home?",
        a: "Yes, with the right stone and finish. Honed limestone, quartzite and bluestone are hard-wearing and forgiving, and a brushed or tumbled surface hides wear far better than a polish. Marble is the material that asks for tolerance — it etches and patinas, which many clients love and some do not. We match the stone to how the household actually lives.",
      },
      {
        q: "Does stone flooring need sealing?",
        a: "Natural stone is porous, so an impregnating sealer is applied on installation and re-applied periodically depending on traffic and exposure. Sealing does not make stone bulletproof; it buys you time to wipe up spills before they mark. We supply the sealing and cleaning regime with every stone selection.",
      },
      {
        q: "Stone or porcelain that looks like stone?",
        a: "Porcelain is lower maintenance, non-porous and consistent — the sensible choice for a busy wet area or a rental. Natural stone has depth, veining and a patina that develops over decades, and it reads as real underfoot. Many of our projects use both: stone where it is seen and touched, porcelain where it is worked.",
      },
      {
        q: "Can stone be used outside and around a pool in Sydney?",
        a: "Yes. Travertine, bluestone and quartzite are all excellent externally when specified in a sandblasted or tumbled finish with an appropriate slip rating, and coping and pavers can be matched to the internal floor for a continuous line from inside to out. Salt and chlorine exposure influences which stone we recommend.",
      },
      {
        q: "How much does natural stone flooring cost?",
        a: "Stone sits at the upper end of the market, and installation is a larger share of the cost than with other elements because of weight, thickness, cutting and setting-out. Travertine and some limestones are more accessible than bookmatched marble. Bring your plans to Drummoyne and we will give you a realistic supplied-and-installed range.",
      },
      {
        q: "Where can I see stone samples in Sydney?",
        a: "At My Floor Studio, 184 Victoria Road, Drummoyne NSW 2047, by appointment Monday to Saturday. We serve the Inner West, Lower North Shore, Eastern Suburbs, greater Sydney and regional New South Wales, and can arrange full slab viewings for larger projects.",
      },
    ],
    seo: {
      title: "Natural Stone Flooring Sydney · Limestone, Marble & Bluestone — My Floor Studio",
      description:
        "Limestone, marble, travertine, quartzite and bluestone flooring in Sydney, selected slab by slab at our Drummoyne studio. Serving the Inner West and all of New South Wales.",
      keywords:
        "stone flooring sydney, limestone flooring sydney, marble flooring sydney, travertine sydney, bluestone pavers sydney, quartzite flooring, natural stone drummoyne, stone flooring inner west, pool coping stone sydney",
    },
  },
];

export const getElement = (slug) => ELEMENTS.find((e) => e.slug === slug);

// Suburb / region tiers used for the areaServed block on every element page:
// strongest signal closest to Drummoyne, widening to all of NSW.
export const AREAS_SERVED = {
  local: ["Drummoyne", "Five Dock", "Russell Lea", "Rodd Point", "Abbotsford", "Chiswick"],
  inner: ["Balmain", "Rozelle", "Leichhardt", "Haberfield", "Concord", "Annandale", "Newtown"],
  wider: ["Hunters Hill", "Mosman", "Lane Cove", "Paddington", "Woollahra", "Bondi", "Vaucluse"],
  regions: [
    "Inner West Sydney",
    "Lower North Shore",
    "Eastern Suburbs",
    "Northern Beaches",
    "Greater Sydney",
    "New South Wales",
  ],
};
